/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import AgencyReport from './AgencyReport'
import { useMutation, useQuery } from '@tanstack/react-query'
import { downloadAgencyReportData, fetchAgencyReportData } from '../../lib/rest/agencyAPIClient'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import useParsedQueryParam from '../../../../client/js/hooks/useParsedQueryParam'
import { useEffect, useMemo } from 'react'
import { stringify } from 'qs'
import { DateTime, DurationUnit } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PageTitle from '../../../../client/js/components/PageTitle'
import { FormattedDate, useIntl } from 'react-intl'
import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { Box, Button, Card, Container, Divider, SegmentedControl, Stack, Text } from '@mantine/core'
import { Link } from 'react-router-dom'
import { faCalendarDay } from '@fortawesome/free-solid-svg-icons/faCalendarDay'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { useNavigate } from 'react-router'
import ExperimentalBadge from '../../../../client/js/components/ExperimentalBadge'
import Toolbar from '../../../../client/js/components/Toolbar'
import Page from '../../../../client/js/components/Page'

function AgencyReportPage () {
  const intl = useIntl()
  const navigate = useNavigate()
  const year = useParsedQueryParam('year', Number)
  const month = useParsedQueryParam('month', Number)
  const unit: DurationUnit = month > 0 ? 'month' : 'year'

  const currentDt = DateTime.fromObject({ year, month })
  const prevDt = currentDt.minus({ [unit]: 1 })
  const nextDt = currentDt.plus({ [unit]: 1 })

  const query = useMemo(() => ({
    year, month
  }), [month, year])

  const reportQuery = useQuery({
    enabled: year > 0,
    queryKey: ['agencyReportData', query],
    queryFn: () => fetchAgencyReportData(query),
    refetchOnWindowFocus: true
  })

  const downloadQuery = useMutation({
    mutationKey: ['downloadAgencyReportData', query],
    mutationFn: () => downloadAgencyReportData(query)
  })

  useEffect(() => {
    // Display current month.
    if (year == null || year <= 0) {
      const dt = DateTime.now()
      navigate(
        { search: stringify({ year: dt.year, month: dt.month }) },
        { replace: true }
      )
    }
  }, [navigate, year])

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={intl.formatMessage({
            id: 'agency_report',
            defaultMessage: 'Rapport de l\'agence'
          })}
        />
        <Card shadow="sm">
          <Stack>
            <Toolbar>
              <Button
                disabled={currentDt.hasSame(DateTime.now(), unit)}
                component={Link}
                to={`?${stringify({
                  year: DateTime.now().year,
                  month: unit === 'month' ? DateTime.now().month : undefined
                })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faCalendarDay}
                />
              </Button>
              <Button
                component={Link}
                to={`?${stringify({
                  year: prevDt.year,
                  month: unit === 'month' ? prevDt.month : undefined
                })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronLeft}
                />
              </Button>
              <Text tt="uppercase" fw="bold">
                <FormattedDate
                  value={currentDt.toJSDate()}
                  year="numeric"
                  month={unit === 'month' ? 'long' : undefined}
                />
              </Text>
              <Button
                component={Link}
                to={`?${stringify({
                  year: nextDt.year,
                  month: unit === 'month' ? nextDt.month : undefined
                })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronRight}
                />
              </Button>
              <Button
                disabled={reportQuery.isFetching}
                loading={reportQuery.isFetching}
                onClick={() => {
                  reportQuery.refetch()
                }}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faRefresh}
                />
              </Button>
              <Button
                disabled={downloadQuery.isPending}
                loading={downloadQuery.isPending}
                onClick={() => {
                  downloadQuery.mutate()
                }}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faDownload}
                />
              </Button>
              <SegmentedControl
                data={[
                  {
                    label: intl.formatMessage({
                      id: 'monthly',
                      defaultMessage: 'Mensuel'
                    }),
                    value: 'month'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'yearly',
                      defaultMessage: 'Annuel'
                    }),
                    value: 'year'
                  }
                ]}
                onChange={(v) => {
                  navigate({
                    search: stringify(v === 'month'
                      ? { year, month: 1 }
                      : { year })
                  })
                }}
                value={unit}
              />
              <Box ms="auto">
                <ExperimentalBadge />
              </Box>
            </Toolbar>

            <Divider />

            <LoadingHelper
              query={reportQuery}
              render={() => (
                <AgencyReport data={reportQuery.data} />
              )}
            />
          </Stack>
        </Card>
      </Container>
    </Page>
  )
}

export default AgencyReportPage
